import { isCurrentApprovedLeave } from "./currentLeaveHelpers";
import { getRequestStatusLabel } from "./requestHelpers";

const REJECTED_STATUSES = new Set([
  "rejected",
  "manager_rejected",
  "dm_rejected",
  "cancelled",
]);

const OVERTIME_SETTLEMENT_LABELS = {
  PAID_OT: "صرف مالي",
  COMPENSATORY: "إذن تعويضي",
};

export function isOwnEmployeeRequest(request, user) {
  if (!request || !user) return false;
  if (user.id && request.userId) {
    return String(request.userId) === String(user.id);
  }
  if (user.employeeId && request.employeeId) {
    return String(request.employeeId) === String(user.employeeId);
  }
  const code = user.code;
  return Boolean(code && request.employeeCode && request.employeeCode === code);
}

export function isLeaveLikeRequest(request) {
  if (!request) return false;
  if (request._source === "leave" || request._source === "permit") return true;
  const type = request.type || "";
  return (
    type.includes("إجازة") ||
    type.includes("مرضية") ||
    type === "إذن" ||
    type === "إذن تعويضي"
  );
}

export function isOvertimeRequest(request) {
  return request?._source === "overtime" || request?.type === "إضافي";
}

export function filterTeamLeaveRequests(pendingRequests, user) {
  return (pendingRequests || []).filter(
    (request) =>
      isLeaveLikeRequest(request) && !isOwnEmployeeRequest(request, user),
  );
}

export function filterTeamOvertimeRequests(pendingRequests, user) {
  return (pendingRequests || []).filter(
    (request) =>
      isOvertimeRequest(request) && !isOwnEmployeeRequest(request, user),
  );
}

export function filterCurrentlyOnLeaveRequests(requests, dateIso) {
  return (requests || []).filter((request) =>
    isCurrentApprovedLeave(request, dateIso),
  );
}

export function matchesTeamRequestSearch(request, searchTerm) {
  const query = String(searchTerm || "").trim().toLowerCase();
  if (!query) return true;
  const haystack = [
    request?.employeeName,
    request?.employeeCode,
    request?.type,
    request?.workplaceLabel,
    request?.branchName,
    request?.departmentName,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

export function matchesTeamRequestStatus(request, statusFilter) {
  if (!statusFilter || statusFilter === "all") return true;
  if (statusFilter === "pending") {
    return (
      request?.status === "pending" ||
      request?.status === "gm_pending" ||
      request?.status === "manager_approved" ||
      request?.status === "modified"
    );
  }
  if (statusFilter === "rejected") {
    return REJECTED_STATUSES.has(request?.status);
  }
  return request?.status === statusFilter;
}

export function requestStatusUiKey(status) {
  if (status === "approved") return "approved";
  if (REJECTED_STATUSES.has(status)) return "rejected";
  return "review";
}

export function requestStatusBadgeLabel(status) {
  return getRequestStatusLabel(status);
}

export function overtimeSettlementLabel(value) {
  if (!value) return "";
  return OVERTIME_SETTLEMENT_LABELS[value] || value;
}

export function formatLeaveDays(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export function leaveRemainingTone(remaining) {
  const n = Number(remaining);
  if (!Number.isFinite(n) || n <= 0) return "empty";
  if (n <= 2) return "low";
  return "ok";
}

export function getEmployeeLeaveRemaining(employee) {
  const leave = employee?.leaveBalance || {};
  const annualEntitlement = Number(leave.annualEntitlement ?? 0);
  const annualUsed = Number(leave.annualUsed ?? 0);
  const casualEntitlement = Number(leave.casualEntitlement ?? 0);
  const casualUsed = Number(leave.casualUsed ?? 0);
  const sickEntitlement = Number(leave.sickEntitlement ?? 0);
  const sickUsed = Number(leave.sickUsed ?? 0);

  return {
    annualEntitlement,
    annualUsed,
    annualRemaining: Math.max(
      0,
      Number(leave.annualRemaining ?? annualEntitlement - annualUsed),
    ),
    casualEntitlement,
    casualUsed,
    casualRemaining: Math.max(
      0,
      Number(leave.casualRemaining ?? casualEntitlement - casualUsed),
    ),
    sickEntitlement,
    sickUsed,
    sickRemaining: Math.max(
      0,
      Number(leave.sickRemaining ?? sickEntitlement - sickUsed),
    ),
  };
}

export function formatTeamLeaveRemainingSummary(employee) {
  if (!employee) return "—";
  const bal = getEmployeeLeaveRemaining(employee);
  return `اعتيادية ${formatLeaveDays(bal.annualRemaining)} · عارضة ${formatLeaveDays(bal.casualRemaining)}`;
}

export function matchesTeamMemberSearch(member, searchTerm) {
  const query = String(searchTerm || "").trim().toLowerCase();
  if (!query) return true;
  const haystack = [
    member?.name,
    member?.code,
    member?.jobTitle,
    member?.departmentName,
    member?.branchName,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

export function formatTeamRequestDetails(request) {
  if (!request) return "—";

  if (
    request._source === "permit" ||
    request.type === "إذن" ||
    request.type === "إذن تعويضي"
  ) {
    const hours = request.hours ? `${request.hours} ساعة` : "";
    const times =
      request.permitStartTime && request.permitEndTime
        ? `${request.permitStartTime} – ${request.permitEndTime}`
        : "";
    const date = request.startDate || "";
    return [hours, times, date].filter(Boolean).join(" · ") || "—";
  }

  if (isOvertimeRequest(request)) {
    const hours = `${Number(request.hours || 0).toFixed(2)} ساعة`;
    const range =
      request.endDate && request.endDate !== request.startDate
        ? `${request.startDate} → ${request.endDate}`
        : request.startDate || "";
    return [hours, range].filter(Boolean).join(" · ") || "—";
  }

  const days = request.days ? `${request.days} يوم` : "";
  const half = request.halfDayPeriodLabel || "";
  const range =
    request.endDate && request.endDate !== request.startDate
      ? `${request.startDate} → ${request.endDate}`
      : request.startDate || "";
  return [days, half, range].filter(Boolean).join(" · ") || "—";
}
